import { Injectable } from '@angular/core';

import { map, Observable, of } from 'rxjs';
import { HttpServiceHelper } from '@reg/shared/service';
import { IdentitySeasonBodyAssessSetting } from '@reg/shared/model/identity/identity-season-body-assess-setting.model';
import { IJsonApiResponse } from '@reg/shared/json-api/json-api.interface';
import { observableInSeparateThread } from '@reg/shared/helper/observable-helper';

/**
 * Service to get season related body assessment settings.
 */
@Injectable()
export class IdentitySeasonBodyAssessSettingService {
    public static get INCLUDE_BODY_ASSESS_MEASUREMENT(): string {
        return 'bodyAssessmentMeasurement';
    }

    private identityApiBaseUrl: string;
    private cachedSettingsMap: { [apiRequest: string]: IdentitySeasonBodyAssessSetting[]; } = {};

    /**
     * Constructor
     */
    constructor(private http: HttpServiceHelper) {
        this.identityApiBaseUrl = http.configService.getBaseConfig('identityApiBaseUrl');
    }

    /**
     * Get all team rosters
     */
    public getIndex(seasonId: string, includes: string[] = null): Observable<IdentitySeasonBodyAssessSetting[]> {
        const apiRequestUrl: string = this.getApiUrl(seasonId, null, includes, null);

        if (this.cachedSettingsMap[apiRequestUrl]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(this.cachedSettingsMap[apiRequestUrl].map(
                (setting: IdentitySeasonBodyAssessSetting) => setting.clone(),
            ));
        }
        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const settings: IdentitySeasonBodyAssessSetting[] = [];
                const included: Object[] = response.included;

                const bodyData: any = response.data || [];
                for (const setting of bodyData) {
                    settings.push((new IdentitySeasonBodyAssessSetting()).deserialize(setting, included));
                }

                this.cachedSettingsMap[apiRequestUrl] = settings;
                return settings.map((setting: IdentitySeasonBodyAssessSetting) => setting.clone());
            }));
    }

    /**
     * Get a single team roster
     */
    public get(
        seasonId: string,
        seasonBodyAssessSettingId: string,
        includes: string[] = null,
    ): Observable<IdentitySeasonBodyAssessSetting> {
        const apiRequestUrl: string = this.getApiUrl(seasonId, seasonBodyAssessSettingId, includes, null);

        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => (new IdentitySeasonBodyAssessSetting().deserialize(response))));
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(seasonId: string, settingId: string, includes: string[], queryParams: string[] = null): string {
        let url: string = `${this.identityApiBaseUrl}/seasons/${seasonId}/settings/`;

        if (settingId) {
            url += settingId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }
}

